/**
 * Offset a sticky page element has to sit at so it clears the fixed chrome
 * painted over the top of the viewport (app bar, safe-area system bar).
 *
 * Vuetify's `--v-layout-top` is not usable for this. An app bar with
 * `scroll-behavior="hide"` keeps its extension (the tabs row) on screen but
 * marks the layout item inactive, so the variable reports 0 while the
 * extension is still occupying the top of the viewport. Measuring the bar's
 * own bottom edge is the only signal that stays true in every scroll state.
 *
 * Sticky elements stack: pass the rect of one that pins above and the offset
 * clears that too, so the two never overlap.
 *
 * Only applies to bars the page itself scrolls. A bar inside a scrolling
 * element pins flush to that box instead: the box declares `--sticky-top: 0`
 * and the bar reads it from CSS, no measurement involved.
 *
 * @param {DOMRect|null|undefined} appBarRect bounding rect of the app bar, if present
 * @param {DOMRect|null|undefined} systemBarRect bounding rect of the safe-area
 *   system bar (App.vue renders one sized to `env(safe-area-inset-top)` on
 *   notched devices), if present
 * @param {DOMRect|null|undefined} pinnedAboveRect bounding rect of a sticky
 *   element that pins above this one (the branding band above the filter bar),
 *   if present
 * @returns {number|null} offset in px, or null when there is no chrome to clear
 */
export const resolveStickyTop = (appBarRect, systemBarRect, pinnedAboveRect) => {
  if (!appBarRect && !systemBarRect) return null

  // The safe-area system bar is fixed and paints over the notch strip, so
  // pinning to the viewport top would park the element underneath it. Layouts
  // without an app bar (the focus layout the note editor runs in) still have to
  // clear it.
  const floor = systemBarRect ? Math.round(systemBarRect.bottom) : 0

  // The bar is translated off-screen rather than unmounted, so its bottom goes
  // negative once fully hidden -- clamp so the element pins below the notch.
  const top = Math.max(floor, appBarRect ? Math.round(appBarRect.bottom) : 0)

  if (!pinnedAboveRect) return top

  // Height, not `bottom`. The element above pins at this same offset, so its
  // settled bottom is always `top + height` -- while its own `bottom` is still
  // travelling with the page until it pins. Feeding a moving value back in
  // would leave this bar chasing it a frame behind, parked below where it
  // belongs whenever scroll events batch (iOS momentum scrolling).
  return top + Math.round(pinnedAboveRect.height)
}
